/* eslint-disable @typescript-eslint/no-explicit-any */

import { useBenefitDocumentsPartialUpdate } from '@ben/api/gen/codegen';
import { uploadBenefitDocuments } from '@ben/columbia/api/upload-benefit-documents';
import { Modal, ModalVariant } from '@ben/orion/modals';
import Banner from '@ben/orion/banner';
import { ButtonVariant } from '@ben/orion/deprecated-button';
import { FileUploadList } from '@ben/orion/file-upload-lists';
import FileUploader from '@ben/orion/file-uploader';
import { IconName } from '@ben/orion/icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import type { EmployerBenefitDocument } from '@ben/shared/types/benefits';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { convertUrlToFile } from '@ben/shared/utils/convert-url-into-file';
import { useMutation } from '@tanstack/react-query';
import type { Dispatch, FC, SetStateAction } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css, useTheme } from 'styled-components';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import DocumentUploadForm from './document-upload-form';

interface IBenefitDocuments {
  isCloneBenefit: boolean;
  setIsUploadInprogress: Dispatch<SetStateAction<boolean>>;
}

const BenefitDocuments: FC<IBenefitDocuments> = ({
  isCloneBenefit,
  setIsUploadInprogress,
}) => {
  const { control } = useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();

  const documents = useWatch({ control, name: 'documents', exact: true });

  const [currentDocuments, setCurrentDocuments] = useState<any>(documents);
  const [showDocumentUpload, setShowDocumentUpload] = useState(true);
  const [editDocument, setEditDocument] = useState(null);
  const [deleteDocumentInfo, setDeleteDocumentInfo] = useState({
    id: '',
    name: '',
  });
  const deleteDocumentConfirmationModalRef = useRef<IModalRef>(null);
  const editDocumentModalRef = useRef<IModalRef>(null);
  const theme = useTheme();

  useEffect(() => {
    setIsUploadInprogress(!showDocumentUpload);
  }, [showDocumentUpload, setIsUploadInprogress]);

  useEffect(() => {
    if (!isCloneBenefit) return;

    const uploadDocuments = async () => {
      setCurrentDocuments([]);
      if (!documents) return;

      await Promise.all(
        documents.map(async (document: any) => {
          const file = await convertUrlToFile({
            url: document?.['file'],
          });
          uploadDocumentMutation.mutate({
            name: document?.['name'],
            description: document?.['description'],
            file: file,
            isPolicyDocument: document?.['isPolicyDocument'],
          });
        })
      );
    };

    void uploadDocuments().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloneBenefit]);

  const uploadDocumentMutation = useMutation(
    (data: EmployerBenefitDocument) =>
      uploadBenefitDocuments(
        {
          ...data,
          // eslint-disable-next-line camelcase
          is_policy_document: data?.['isPolicyDocument'],
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      ),
    {
      onSuccess: (data) => {
        setShowDocumentUpload(true);

        setCurrentDocuments((previousState: EmployerBenefitDocument[]) => {
          const employeeDocumentsWithoutFile = previousState?.filter(
            (document: EmployerBenefitDocument) => !(document instanceof File)
          );
          const nextDocuments = [
            ...(employeeDocumentsWithoutFile || []),
            data as unknown as EmployerBenefitDocument,
          ];
          handleSetDocuments(nextDocuments);
          return nextDocuments;
        });
      },
      onError: () => {
        toast.error('Error uploading document, please try again.');
      },
    }
  );

  const updateDocumentMutation = useBenefitDocumentsPartialUpdate({
    mutation: {
      onSuccess: () => {
        toast.success('Document successfully updated');
      },
      onError: () => {
        toast.error('Error updating document, please try again.');
      },
    },
  });

  const saveDocument = (documentUpdate: EmployerBenefitDocument) => {
    updateDocumentMutation.mutate({
      benefitDocumentPk: Number(editDocument?.['id']),
      data: {
        name: documentUpdate?.['name'],
        description: documentUpdate?.['description'],
        isPolicyDocument: documentUpdate?.['isPolicyDocument'],
      },
    });
    editDocumentModalRef.current?.hideModal();
    setCurrentDocuments((previousState: EmployerBenefitDocument[]) => {
      const employeeDocumentsWithoutFile = previousState?.filter(
        (document: EmployerBenefitDocument) =>
          document?.['id'] !== editDocument?.['id']
      );
      const nextDocuments = [
        ...(employeeDocumentsWithoutFile || []),
        {
          ...documentUpdate,
          file: editDocument?.['file'],
          id: editDocument?.['id'],
        },
      ];
      handleSetDocuments(nextDocuments);
      return nextDocuments;
    });
  };

  const handleSetDocuments = useCallback(
    (nextDocuments: EmployerBenefitDocument[], showToast = true) => {
      if (showToast) {
        toast.info(
          'Documents changed successfully, but please save the benefit to reflect the changes.'
        );
      }
      setDirtyValue('documents', nextDocuments);
    },
    [setDirtyValue]
  );

  return (
    <Wrapper $inProgress={!showDocumentUpload}>
      <Typography
        variant={TypographyVariant.h2}
        color={
          showDocumentUpload
            ? theme.palette.neutral[100]
            : theme.palette.error.main
        }
      >
        Benefit documents
      </Typography>
      {showDocumentUpload ? undefined : (
        <Banner
          variant="critical"
          message="You haven’t saved the document. Your changes will be lost if you don’t save them."
          hideIcon
        />
      )}
      <ListContainer>
        {currentDocuments
          ?.filter(
            (document: EmployerBenefitDocument) => !(document instanceof File)
          )
          .map((document: EmployerBenefitDocument, index: number) => (
            <FileUploadList
              key={index}
              maxTitleLength={44}
              files={[
                {
                  originalName: document?.['name'],
                  description: document?.['description'],
                  uuid: document?.['id'] as string,
                  file: document?.['file'] as unknown as string,
                  isPolicyDocument: document?.['isPolicyDocument'],
                },
              ]}
              onEdit={(id) => {
                const documentToEdit = currentDocuments?.find(
                  (document: any) => document?.['id'] === id
                );
                setEditDocument(documentToEdit);
                editDocumentModalRef.current?.showModal();
              }}
              onRemove={(id) => {
                const documentName = currentDocuments?.find(
                  (document: any) => document?.['id'] === id
                )?.['name'];
                setDeleteDocumentInfo({
                  id,
                  name: documentName,
                });
                deleteDocumentConfirmationModalRef.current?.showModal();
              }}
              showPolicyDocumentInfo
            />
          ))}
      </ListContainer>
      {showDocumentUpload ? (
        <FileUploader
          uploading={false}
          maxFilesNumber={1}
          maxFileSize={15}
          onFileUpload={(file) => {
            setCurrentDocuments(
              Array.isArray(file)
                ? [...(currentDocuments || []), ...file]
                : file
            );
            setShowDocumentUpload(false);
          }}
          acceptedFileExtensions={['pdf', 'doc', 'txt', 'csv']}
        />
      ) : (
        <DocumentUploadForm
          document={{
            file: currentDocuments?.at(-1) as File,
            name: (currentDocuments?.at(-1) as File).name,
            description: '',
            isPolicyDocument: false,
          }}
          isInitialUpload
          onDelete={() => {
            currentDocuments?.pop();
            handleSetDocuments(currentDocuments, false);
            setShowDocumentUpload(true);
          }}
          onSave={(documentUploadForm: EmployerBenefitDocument) => {
            currentDocuments?.pop();
            setShowDocumentUpload(true);
            uploadDocumentMutation.mutate(documentUploadForm);
          }}
        />
      )}

      <Modal
        ref={deleteDocumentConfirmationModalRef}
        title={"Delete '" + deleteDocumentInfo?.name + "'"}
        description={
          "Are you sure you want to delete the file '" +
          deleteDocumentInfo.name +
          "'?"
        }
        primaryText={'Delete'}
        primaryVariant={ButtonVariant.error}
        secondaryText={'Cancel'}
        onConfirm={() => {
          const documentsWithoutFileToBeDeleted = currentDocuments?.filter(
            (document: any) => document?.['id'] !== deleteDocumentInfo.id
          );
          const nextDocuments = [...(documentsWithoutFileToBeDeleted || [])];
          setCurrentDocuments(nextDocuments);
          handleSetDocuments(nextDocuments, false);
          deleteDocumentConfirmationModalRef.current?.hideModal();
          toast.success('Document successfully deleted');
        }}
        icon={IconName.Trash}
        variant={ModalVariant.Icon}
        height="340px"
        width="520px"
      />
      {editDocument && (
        <Modal
          ref={editDocumentModalRef}
          title={'Edit document'}
          onClose={() => setEditDocument(null)}
          height="fit-content"
          width="520px"
        >
          <DocumentUploadForm
            document={editDocument}
            onDelete={() => {
              editDocumentModalRef.current?.hideModal();
            }}
            onSave={(documentUpdate) => {
              saveDocument(documentUpdate);
              handleSetDocuments(currentDocuments, false);
            }}
            editing
          />
        </Modal>
      )}
    </Wrapper>
  );
};

export default memo(BenefitDocuments);

const Wrapper = styled.div<{
  $inProgress: boolean;
}>`
  ${({ theme, $inProgress }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border-radius: 10px;
    border: 1px solid
      ${$inProgress ? theme.palette.error.main : theme.palette.neutral[60]};
    gap: ${theme.spacing(2)};
  `}
`;

const ListContainer = styled.ul`
  padding-left: 0;
  margin: 0;
`;
